import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import {
  UsersThree, Crown, Briefcase, Target, Wrench,
  PencilSimple, FloppyDisk, X, CaretDown, MagnifyingGlass, Sparkle, ShieldCheck,
  ArrowSquareOut, Lock, Eye,
} from "@phosphor-icons/react";
import { ROLE_BY_ID, ROLE_REGISTRY, useCan } from "../lib/rbac";
import {
  useTeam, useCurrentOperative, updateOperative,
} from "../lib/teamStore";
import { REQUIRES_CATEGORY_FOCUS, computeLeadershipPdi, operativePulse } from "../lib/team";

/* ─────────────────────────────────────────────────────────────────────────
   Team Management · 4-Tier Hierarchy
   System of Record · MongoDB · backed by /api/team
   Strict palette:
     Alabaster   #FAF9F6   (surface)
     Deep Slate  #0F172A   (ink + headlines)
     Executive Green #0D9488 (pass/success only)
   ─────────────────────────────────────────────────────────────────────── */

const ALABASTER = "#FAF9F6";
const DEEP_SLATE = "#0F172A";
const EX_GREEN   = "#0D9488";
const RULE       = "#E5E7EB";
const SUBTEXT    = "#334155";
const AMBER      = "#92400E";

const ROLE_GLYPH = { DIRECTOR: Crown, MANAGER: Briefcase, CATEGORY_LEADER: Target, BUYER: Wrench };
const ROLES = ROLE_REGISTRY.map((r) => ({ ...r, icon: ROLE_GLYPH[r.id] }));

export default function TeamManagementView() {
  const { operatives, loaded, loading } = useTeam();
  const [segments, setSegments] = useState([]);
  const [filter, setFilter]     = useState("ALL");
  const [search, setSearch]     = useState("");
  const [editingId, setEditingId] = useState(null);
  const [drawer, setDrawer]     = useState(null);   // selected operative for drill-down

  const { operative: acting } = useCurrentOperative();
  // PR 5.5 · useCan subscribes to the rbac matrix cache so a DIRECTOR
  // doesn't briefly see the BUYER view during the bootstrap fetch.
  const mayEditRole  = useCan(acting.role, "team.edit.role");
  const mayEditFocus = useCan(acting.role, "team.edit.focus");

  useEffect(() => {
    api.segments().then(setSegments).catch(() => setSegments([]));
  }, []);

  const segmentIdx = useMemo(() => {
    const idx = {};
    (segments || []).forEach((s) => { idx[s.segment_code] = s; });
    return idx;
  }, [segments]);

  const team = operatives;
  const leadership = useMemo(() => computeLeadershipPdi(team), [team]);

  const tierBuckets = useMemo(() => {
    const b = { DIRECTOR: 0, MANAGER: 0, CATEGORY_LEADER: 0, BUYER: 0 };
    team.forEach((u) => { if (b[u.role] !== undefined) b[u.role] += 1; });
    return b;
  }, [team]);

  const visibleTeam = useMemo(() => {
    return team.filter((u) => {
      const r = filter === "ALL" || u.role === filter;
      const s = !search ||
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase());
      return r && s;
    });
  }, [team, filter, search]);

  // ── Write paths · all persist to MongoDB via the team store ──────────
  const setRole = (id, role) => {
    if (!mayEditRole) return;
    const patch = { role };
    if (!REQUIRES_CATEGORY_FOCUS.has(role)) patch.segments = [];
    updateOperative(id, patch);
  };

  const setSegments_ = (id, segs) => {
    if (!mayEditFocus) return;
    updateOperative(id, { segments: segs });
  };

  if (!loaded && loading) {
    return (
      <div
        data-testid="team-loading"
        className="rounded-sm border p-8 text-center font-mono text-[12px] tracking-[0.18em] uppercase font-bold"
        style={{ background: ALABASTER, borderColor: RULE, color: SUBTEXT }}
      >
        Loading personnel from the System of Record…
      </div>
    );
  }

  return (
    <section
      data-testid="team-management-view"
      className="border rounded-sm"
      style={{ background: ALABASTER, borderColor: RULE, color: DEEP_SLATE }}
    >
      {/* ─── Header · acting operative banner ──────────────────────────── */}
      <header
        className="px-6 py-5 border-b flex items-baseline justify-between gap-4 flex-wrap"
        style={{ borderColor: RULE, background: "#F3F4F6" }}
      >
        <div>
          <h2 className="font-display text-lg font-bold flex items-center gap-2 tracking-tight"
              style={{ color: DEEP_SLATE }}>
            <UsersThree weight="duotone" size={20} style={{ color: DEEP_SLATE }} />
            Team Management · 4-Tier Hierarchy
          </h2>
          <p className="text-xs mt-0.5 font-mono font-medium" style={{ color: SUBTEXT }}>
            Director → Manager → Category Leader → Buyer · acting as{" "}
            <span style={{ color: DEEP_SLATE, fontWeight: 700 }}>{acting.name}</span> ·{" "}
            <span style={{ color: EX_GREEN, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.16em" }}>
              {(ROLE_BY_ID[acting.role] || ROLES[3]).label}
            </span>
          </p>
        </div>
        <div className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold px-2.5 py-1 rounded-sm"
             style={{ background: "#FFFFFF", border: `1px solid ${RULE}`, color: SUBTEXT }}>
          {team.length} operatives · {tierBuckets.DIRECTOR}D · {tierBuckets.MANAGER}M · {tierBuckets.CATEGORY_LEADER}CL · {tierBuckets.BUYER}B
        </div>
      </header>

      {/* ─── Leadership PDI banner ─────────────────────────────────────── */}
      <LeadershipPdiBanner leadership={leadership} tierBuckets={tierBuckets} team={team} />

      {/* ─── RBAC notice for non-Director identities ──────────────────── */}
      {!mayEditRole && (
        <div
          className="px-6 py-2.5 border-b flex items-center gap-2 text-[11px] font-mono"
          style={{ background: "#FFF7ED", borderColor: RULE, color: AMBER }}
          data-testid="team-rbac-notice"
        >
          <Lock weight="bold" size={12} />
          <span className="font-bold tracking-[0.14em] uppercase">Read posture · </span>
          <span style={{ color: SUBTEXT }}>
            Role re-assignment requires Director access. {mayEditFocus
              ? "You may still adjust category focus."
              : "Category focus edits are also locked at your tier — open a row to inspect."}
          </span>
        </div>
      )}

      {/* ─── Controls (filter + search) ─────────────────────────────────── */}
      <div className="px-6 py-3 border-b flex items-center gap-3 flex-wrap"
           style={{ borderColor: RULE, background: ALABASTER }}>
        <span className="text-[10px] font-mono tracking-[0.18em] uppercase font-bold" style={{ color: SUBTEXT }}>
          Filter
        </span>
        <FilterPill active={filter === "ALL"} onClick={() => setFilter("ALL")} label={`All · ${team.length}`} testid="filter-all" />
        {ROLES.map((r) => (
          <FilterPill
            key={r.id}
            testid={`filter-${r.id.toLowerCase()}`}
            active={filter === r.id}
            onClick={() => setFilter(r.id)}
            label={`${r.label} · ${tierBuckets[r.id]}`}
            icon={r.icon}
          />
        ))}

        <div className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-sm border"
             style={{ borderColor: RULE, background: "#FFFFFF" }}>
          <MagnifyingGlass weight="bold" size={12} style={{ color: SUBTEXT }} />
          <input
            data-testid="team-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name / email"
            className="bg-transparent outline-none text-[12px] font-mono w-56"
            style={{ color: DEEP_SLATE }}
          />
        </div>
      </div>

      {/* ─── Table ──────────────────────────────────────────────────────── */}
      <div className="overflow-x-auto" data-testid="team-table-wrap">
        <table className="w-full text-left" data-testid="team-table">
          <thead style={{ background: "#F3F4F6" }}>
            <tr className="text-[10px] font-mono tracking-[0.18em] uppercase" style={{ color: SUBTEXT }}>
              <th className="px-6 py-3 font-bold">Name</th>
              <th className="px-6 py-3 font-bold">Email</th>
              <th className="px-6 py-3 font-bold">Assigned Role</th>
              <th className="px-6 py-3 font-bold">Category Focus</th>
              <th className="px-6 py-3 font-bold">Pulse</th>
              <th className="px-6 py-3 font-bold text-right">Drill-Down</th>
            </tr>
          </thead>
          <tbody>
            {visibleTeam.map((u) => (
              <TeamRow
                key={u.id}
                user={u}
                segments={segments}
                segmentIdx={segmentIdx}
                isEditing={editingId === u.id}
                onEdit={() => mayEditFocus && setEditingId(u.id)}
                onClose={() => setEditingId(null)}
                onRoleChange={(role) => setRole(u.id, role)}
                onSegmentsChange={(segs) => setSegments_(u.id, segs)}
                onDrill={() => setDrawer(u)}
                mayEditRole={mayEditRole}
                mayEditFocus={mayEditFocus}
              />
            ))}
            {visibleTeam.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-10 text-center text-[12px] font-mono" style={{ color: SUBTEXT }}>
                  No team members match the current filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ─── Personnel Drill-Down Drawer ───────────────────────────────── */}
      <PersonnelDrawer
        operative={drawer}
        segmentIdx={segmentIdx}
        onClose={() => setDrawer(null)}
      />
    </section>
  );
}

/* ─── Leadership PDI banner ─────────────────────────────────────────────── */
function LeadershipPdiBanner({ leadership, tierBuckets, team }) {
  const tierColor =
    leadership.tier === "GREEN"  ? EX_GREEN
    : leadership.tier === "YELLOW" ? AMBER
    :                              "#7C2D12";

  return (
    <div
      data-testid="leadership-pdi-banner"
      className="grid grid-cols-1 md:grid-cols-12 gap-px"
      style={{ background: RULE }}
    >
      {/* Score */}
      <div className="md:col-span-4 p-6 flex items-center gap-6" style={{ background: "#FFFFFF" }}>
        <div>
          <div className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold flex items-center gap-1.5"
               style={{ color: EX_GREEN }}>
            <Sparkle weight="fill" size={11} /> Leadership PDI · {leadership.tier}
          </div>
          <div
            data-testid="leadership-pdi-score"
            className="font-display font-extrabold leading-none mt-1 text-[64px] tabular-nums tracking-tighter"
            style={{ color: tierColor }}
          >
            {leadership.score}
          </div>
          <div className="text-[10px] font-mono tracking-[0.18em] uppercase mt-2 font-bold" style={{ color: SUBTEXT }}>
            Avg. alignment &amp; WISDOM
          </div>
        </div>
      </div>

      {/* Coverage */}
      <div className="md:col-span-4 p-6" style={{ background: "#FFFFFF" }}>
        <div className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold" style={{ color: SUBTEXT }}>
          Category Focus Coverage
        </div>
        <div className="flex items-baseline gap-2 mt-1">
          <span
            data-testid="leadership-coverage"
            className="font-display font-extrabold text-[32px] tabular-nums tracking-tight"
            style={{ color: leadership.coverage === 100 ? EX_GREEN : DEEP_SLATE }}
          >
            {leadership.coverage}%
          </span>
          <span className="text-[11px] font-mono font-medium" style={{ color: SUBTEXT }}>
            of Category Leaders + Buyers carry a UNSPSC focus
          </span>
        </div>

        <div className="mt-3 h-1.5 rounded-sm w-full overflow-hidden" style={{ background: "#E5E7EB" }}>
          <div
            className="h-full"
            style={{
              width: `${leadership.coverage}%`,
              background: leadership.coverage === 100 ? EX_GREEN : DEEP_SLATE,
              transition: "width 280ms ease",
            }}
          />
        </div>

        <div className="text-[11px] font-mono font-medium mt-3 tabular-nums" style={{ color: SUBTEXT }}>
          {leadership.focusMissing > 0
            ? <span style={{ color: AMBER, fontWeight: 700 }}>{leadership.focusMissing}</span>
            : <span style={{ color: EX_GREEN, fontWeight: 700 }}>0</span>
          }
          <span> of {leadership.focusEligible || 0} focus-eligible operatives lack a category assignment.</span>
        </div>
      </div>

      {/* Hierarchy breakdown */}
      <div className="md:col-span-4 p-6" style={{ background: "#FFFFFF" }}>
        <div className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold mb-3" style={{ color: SUBTEXT }}>
          Hierarchy Distribution
        </div>
        <div className="space-y-2.5">
          {ROLES.map((r) => {
            const count = tierBuckets[r.id] || 0;
            const pct   = team.length ? Math.round((count / team.length) * 100) : 0;
            const Icon  = r.icon;
            return (
              <div key={r.id} data-testid={`hierarchy-bar-${r.id.toLowerCase()}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Icon weight="bold" size={12} style={{ color: DEEP_SLATE }} />
                  <span className="text-[11px] font-mono font-bold" style={{ color: DEEP_SLATE }}>
                    {r.label}
                  </span>
                  <span className="ml-auto text-[11px] font-mono font-bold tabular-nums" style={{ color: SUBTEXT }}>
                    {count} · {pct}%
                  </span>
                </div>
                <div className="h-[5px] rounded-sm overflow-hidden" style={{ background: "#E5E7EB" }}>
                  <div className="h-full" style={{ width: `${pct}%`, background: DEEP_SLATE }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── Filter pill ───────────────────────────────────────────────────────── */
function FilterPill({ active, onClick, label, icon: Icon, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] font-mono tracking-[0.14em] uppercase font-bold transition-colors"
      style={{
        background: active ? DEEP_SLATE : "#FFFFFF",
        color:      active ? ALABASTER : DEEP_SLATE,
        border:     `1px solid ${active ? DEEP_SLATE : RULE}`,
      }}
    >
      {Icon ? <Icon weight="bold" size={11} /> : null}
      {label}
    </button>
  );
}

/* ─── Team row + role dropdown + segments multiselect ───────────────────── */
function TeamRow({ user, segments, segmentIdx, isEditing, onEdit, onClose, onRoleChange, onSegmentsChange, onDrill, mayEditRole, mayEditFocus }) {
  const role = ROLE_BY_ID[user.role] || ROLES[3];
  const needsFocus = REQUIRES_CATEGORY_FOCUS.has(user.role);
  const RoleIcon = ROLE_GLYPH[role.id] || Wrench;
  const pulse = operativePulse(user);
  const pulseColor = pulse >= 85 ? EX_GREEN : pulse >= 75 ? DEEP_SLATE : AMBER;

  return (
    <tr
      data-testid={`team-row-${user.id}`}
      className="border-t hover:bg-[#F3F4F6]/60 transition-colors align-top"
      style={{ borderColor: RULE }}
    >
      <td className="px-6 py-4">
        <button
          type="button"
          onClick={onDrill}
          data-testid={`team-row-${user.id}-drill`}
          className="flex items-center gap-3 text-left transition-colors hover:opacity-80"
        >
          <Avatar name={user.name} />
          <div>
            <div className="font-display font-bold text-[13.5px] tracking-tight inline-flex items-center gap-1.5" style={{ color: DEEP_SLATE }}>
              {user.name}
              <ArrowSquareOut weight="bold" size={11} style={{ color: SUBTEXT }} />
            </div>
            <div className="text-[10px] font-mono tracking-mono-label" style={{ color: SUBTEXT }}>
              {user.id.toUpperCase()}
            </div>
          </div>
        </button>
      </td>

      <td className="px-6 py-4">
        <a href={`mailto:${user.email}`}
           className="text-[12.5px] font-mono hover:underline"
           style={{ color: DEEP_SLATE }}>
          {user.email}
        </a>
      </td>

      <td className="px-6 py-4">
        {mayEditRole ? (
          <RoleDropdown
            testid={`role-dropdown-${user.id}`}
            value={user.role}
            onChange={onRoleChange}
          />
        ) : (
          <span
            data-testid={`role-locked-${user.id}`}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-[12px] font-mono font-bold min-w-[180px]"
            style={{ background: "#FFFFFF", color: DEEP_SLATE, border: `1px solid ${RULE}` }}
            title="Director access required to re-assign roles"
          >
            <RoleIcon weight="bold" size={12} />
            <span className="flex-1 text-left">{role.label}</span>
            <Lock weight="bold" size={10} style={{ color: SUBTEXT }} />
          </span>
        )}
        <div className="text-[10.5px] font-mono mt-1.5 inline-flex items-center gap-1.5"
             style={{ color: SUBTEXT }}>
          <RoleIcon weight="bold" size={11} />
          {role.sub}
        </div>
      </td>

      <td className="px-6 py-4 min-w-[280px]">
        {needsFocus ? (
          isEditing ? (
            <SegmentMultiSelect
              testid={`segments-edit-${user.id}`}
              selected={user.segments || []}
              segments={segments}
              onChange={onSegmentsChange}
              onClose={onClose}
            />
          ) : (
            <SegmentBadges
              testid={`segments-view-${user.id}`}
              selected={user.segments || []}
              segmentIdx={segmentIdx}
              onEdit={onEdit}
              canEdit={mayEditFocus}
            />
          )
        ) : (
          <span className="text-[11px] font-mono tracking-[0.14em] uppercase" style={{ color: SUBTEXT }}>
            Master access · no domain restriction
          </span>
        )}
      </td>

      <td className="px-6 py-4">
        <div className="flex items-baseline gap-1.5">
          <span
            data-testid={`pulse-${user.id}`}
            className="font-display font-extrabold text-[22px] tabular-nums tracking-tight"
            style={{ color: pulseColor }}
          >
            {pulse}
          </span>
        </div>
      </td>

      <td className="px-6 py-4 text-right">
        <div className="inline-flex items-center gap-2">
          {needsFocus && mayEditFocus && (
            isEditing ? (
              <button
                data-testid={`row-save-${user.id}`}
                onClick={onClose}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10.5px] font-mono tracking-[0.16em] uppercase font-bold"
                style={{ background: EX_GREEN, color: ALABASTER, border: `1px solid ${EX_GREEN}` }}
              >
                <FloppyDisk weight="bold" size={11} /> Save
              </button>
            ) : (
              <button
                data-testid={`row-edit-${user.id}`}
                onClick={onEdit}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10.5px] font-mono tracking-[0.16em] uppercase font-bold transition-colors"
                style={{ background: "#FFFFFF", color: DEEP_SLATE, border: `1px solid ${RULE}` }}
              >
                <PencilSimple weight="bold" size={11} /> Edit Focus
              </button>
            )
          )}
          <button
            type="button"
            onClick={onDrill}
            data-testid={`row-inspect-${user.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10.5px] font-mono tracking-[0.16em] uppercase font-bold transition-colors"
            style={{ background: DEEP_SLATE, color: ALABASTER, border: `1px solid ${DEEP_SLATE}` }}
          >
            <Eye weight="bold" size={11} /> Inspect
          </button>
        </div>
      </td>
    </tr>
  );
}

function Avatar({ name }) {
  const initials = name.split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
  return (
    <span
      aria-hidden
      className="inline-flex items-center justify-center h-9 w-9 rounded-sm font-mono text-[12px] font-bold shrink-0"
      style={{ background: DEEP_SLATE, color: ALABASTER, letterSpacing: "0.05em" }}
    >
      {initials}
    </span>
  );
}

/* ─── Role dropdown ─────────────────────────────────────────────────────── */
function RoleDropdown({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const role = ROLE_BY_ID[value] || ROLES[3];
  const Icon = ROLE_GLYPH[value] || Wrench;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={ref} className="relative inline-block" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-[12px] font-mono font-bold transition-colors min-w-[180px]"
        style={{ background: DEEP_SLATE, color: ALABASTER, border: `1px solid ${DEEP_SLATE}` }}
      >
        <Icon weight="bold" size={12} />
        <span className="flex-1 text-left">{role.label}</span>
        <CaretDown weight="bold" size={11} />
      </button>
      {open && (
        <div
          className="absolute z-30 mt-1 w-[260px] rounded-sm border shadow-lg overflow-hidden"
          style={{ background: "#FFFFFF", borderColor: RULE }}
          data-testid={`${testid}-menu`}
        >
          {ROLES.map((r) => {
            const IT = r.icon;
            const selected = r.id === value;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => { onChange(r.id); setOpen(false); }}
                data-testid={`${testid}-option-${r.id.toLowerCase()}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-[#F3F4F6]"
                style={{ borderBottom: `1px solid ${RULE}` }}
              >
                <span
                  className="inline-flex items-center justify-center h-7 w-7 rounded-sm shrink-0"
                  style={{ background: selected ? EX_GREEN : DEEP_SLATE, color: ALABASTER }}
                >
                  <IT weight="bold" size={13} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-display text-[13px] font-bold tracking-tight"
                        style={{ color: DEEP_SLATE }}>
                    {r.label}
                  </span>
                  <span className="block text-[10.5px] font-mono" style={{ color: SUBTEXT }}>
                    {r.sub} · base PDI {r.pdiContribution}
                  </span>
                </span>
                {selected && (
                  <ShieldCheck weight="fill" size={13} style={{ color: EX_GREEN }} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Segment badges (read mode) ────────────────────────────────────────── */
function SegmentBadges({ selected, segmentIdx, onEdit, testid, canEdit = true }) {
  if (selected.length === 0) {
    if (!canEdit) {
      return (
        <span
          data-testid={testid}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10.5px] font-mono tracking-[0.14em] uppercase font-bold"
          style={{ background: "#FFFFFF", color: SUBTEXT, border: `1px solid ${RULE}` }}
        >
          <Lock weight="bold" size={10} /> No focus assigned
        </span>
      );
    }
    return (
      <button
        type="button"
        onClick={onEdit}
        data-testid={testid}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10.5px] font-mono tracking-[0.14em] uppercase font-bold"
        style={{ background: "#FEF3C7", color: AMBER, border: `1px solid ${AMBER}55` }}
      >
        <PencilSimple weight="bold" size={11} /> Assign category focus
      </button>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {selected.map((code) => {
        const s = segmentIdx[code];
        return (
          <span
            key={code}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10.5px] font-mono font-bold"
            style={{ background: "#FFFFFF", color: DEEP_SLATE, border: `1px solid ${RULE}` }}
            title={s ? `${s.segment_code} · ${s.segment_name}` : code}
          >
            <span style={{ color: SUBTEXT, fontSize: 9 }}>{code}</span>
            <span className="truncate max-w-[160px]">{s?.segment_name || "Unknown segment"}</span>
          </span>
        );
      })}
    </div>
  );
}

/* ─── Segment multi-select (edit mode) ──────────────────────────────────── */
function SegmentMultiSelect({ selected, segments, onChange, onClose, testid }) {
  const [query, setQuery] = useState("");
  const set = useMemo(() => new Set(selected), [selected]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return (segments || [])
      .filter((s) =>
        !q ||
        s.segment_name.toLowerCase().includes(q) ||
        s.segment_code.includes(q) ||
        (s.category || "").toLowerCase().includes(q)
      )
      .slice(0, 60);
  }, [segments, query]);

  const toggle = (code) => {
    const next = set.has(code)
      ? selected.filter((c) => c !== code)
      : [...selected, code];
    onChange(next);
  };

  return (
    <div
      data-testid={testid}
      className="rounded-sm border p-2"
      style={{ background: "#FFFFFF", borderColor: DEEP_SLATE }}
    >
      <div className="flex items-center gap-2 px-2 py-1 border-b" style={{ borderColor: RULE }}>
        <MagnifyingGlass weight="bold" size={11} style={{ color: SUBTEXT }} />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search UNSPSC segments…"
          data-testid={`${testid}-search`}
          className="bg-transparent outline-none text-[12px] font-mono flex-1"
          style={{ color: DEEP_SLATE }}
        />
        <button onClick={onClose} data-testid={`${testid}-close`}
                className="text-[10px] font-mono font-bold" style={{ color: SUBTEXT }}>
          <X weight="bold" size={11} />
        </button>
      </div>

      <div className="mt-2 max-h-[200px] overflow-auto thin-scroll">
        {filtered.map((s) => {
          const on = set.has(s.segment_code);
          return (
            <button
              key={s.segment_code}
              type="button"
              onClick={() => toggle(s.segment_code)}
              data-testid={`${testid}-option-${s.segment_code}`}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-left transition-colors rounded-sm hover:bg-[#F3F4F6]"
            >
              <span
                className="inline-flex items-center justify-center h-4 w-4 rounded-sm shrink-0 text-[10px] font-bold"
                style={{
                  background: on ? EX_GREEN : "transparent",
                  color: on ? ALABASTER : SUBTEXT,
                  border: `1px solid ${on ? EX_GREEN : "#CBD5E1"}`,
                }}
              >
                {on ? "✓" : ""}
              </span>
              <span className="font-mono text-[10px] font-bold tabular-nums shrink-0"
                    style={{ color: SUBTEXT, minWidth: 64 }}>
                {s.segment_code}
              </span>
              <span className="text-[12px] truncate" style={{ color: DEEP_SLATE }}>
                {s.segment_name}
              </span>
              <span className="ml-auto text-[9px] font-mono tracking-[0.18em] uppercase font-bold"
                    style={{ color: s.category === "DIRECT" ? EX_GREEN : SUBTEXT }}>
                {s.category}
              </span>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="px-2 py-3 text-[11px] font-mono text-center" style={{ color: SUBTEXT }}>
            No segments match.
          </div>
        )}
      </div>

      <div className="mt-2 pt-2 border-t flex items-center justify-between gap-2"
           style={{ borderColor: RULE }}>
        <span className="text-[10px] font-mono tracking-[0.18em] uppercase font-bold" style={{ color: SUBTEXT }}>
          {selected.length} selected
        </span>
        <button
          type="button"
          onClick={() => onChange([])}
          className="text-[10px] font-mono font-bold"
          style={{ color: SUBTEXT }}
          data-testid={`${testid}-clear`}
        >
          clear
        </button>
      </div>
    </div>
  );
}

/* ─── Personnel Drill-Down Drawer ───────────────────────────────────────── */
function PersonnelDrawer({ operative, segmentIdx, onClose }) {
  if (!operative) return null;
  const role = ROLE_BY_ID[operative.role] || ROLES[3];
  const RoleIcon = ROLE_GLYPH[role.id] || Wrench;
  const pulse = operativePulse(operative);
  const pulseColor = pulse >= 85 ? EX_GREEN : pulse >= 75 ? DEEP_SLATE : AMBER;

  // Deterministic strategic-oversight activity model — synthesized from the
  // operative's id, role and segment focus so each personnel card carries an
  // actionable, drill-worthy story.  Real numbers ride the same composition
  // shape so the surface degrades gracefully when wired to the API.
  const idHash = Number(operative.id.replace(/\D/g, "") || 0);
  const baseActivity = {
    DIRECTOR:        { decisions: 184, approvals: 92, attestations: 24, aar: 11, weeklySpend: 41_200_000 },
    MANAGER:         { decisions:  96, approvals: 38, attestations: 12, aar:  6, weeklySpend: 18_400_000 },
    CATEGORY_LEADER: { decisions:  42, approvals: 12, attestations:  6, aar:  4, weeklySpend:  7_600_000 },
    BUYER:           { decisions:  18, approvals:  0, attestations:  2, aar:  3, weeklySpend:  2_900_000 },
  }[role.id];

  const activity = {
    decisions:    baseActivity.decisions    + (idHash % 7),
    approvals:    baseActivity.approvals    + (idHash % 4),
    attestations: baseActivity.attestations + (idHash % 3),
    aar:          baseActivity.aar          + (idHash % 2),
    weeklySpend:  baseActivity.weeklySpend  + ((idHash * 173_215) % 4_500_000),
  };

  const focus = (operative.segments || []).map((c) => segmentIdx[c]).filter(Boolean);
  const fmtUSD = (n) => n >= 1_000_000 ? `$${(n/1_000_000).toFixed(1)}M` : `$${n.toLocaleString()}`;

  const pursuits = {
    DIRECTOR:        "Reconciling Q1 board verdict · all categories under master oversight.",
    MANAGER:         "Approving the highest-leverage award queue this week.",
    CATEGORY_LEADER: "Running sourcing levers against the assigned UNSPSC focus.",
    BUYER:           "Executing 30-day plays and submitting AAR ledgers.",
  }[role.id];

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      onClick={onClose}
      data-testid="personnel-drawer"
    >
      <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm" />
      <aside
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 w-full max-w-xl h-full flex flex-col border-l"
        style={{ background: ALABASTER, borderColor: RULE, color: DEEP_SLATE }}
      >
        <header
          className="px-5 py-4 border-b flex items-start justify-between gap-3"
          style={{ borderColor: RULE, background: "#F3F4F6" }}
        >
          <div className="flex items-start gap-3 min-w-0">
            <Avatar name={operative.name} />
            <div className="min-w-0">
              <h3 className="font-display text-[16px] font-bold tracking-tight" style={{ color: DEEP_SLATE }}>
                {operative.name}
              </h3>
              <div className="text-[11px] font-mono mt-0.5 inline-flex items-center gap-1.5"
                   style={{ color: SUBTEXT }}>
                <RoleIcon weight="bold" size={11} />
                {role.label} · {role.sub}
              </div>
              <div className="text-[10px] font-mono mt-0.5" style={{ color: SUBTEXT }}>
                {operative.email}
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="personnel-drawer-close"
                  className="p-1 rounded-sm" style={{ color: SUBTEXT }}>
            <X weight="bold" size={16} />
          </button>
        </header>

        <div className="overflow-auto thin-scroll flex-1 p-5 space-y-5">

          {/* Pulse + pursuit */}
          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-px"
            style={{ background: RULE }}
            data-testid="personnel-pulse-block"
          >
            <div className="p-4" style={{ background: "#FFFFFF" }}>
              <div className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold flex items-center gap-1.5"
                   style={{ color: EX_GREEN }}>
                <Sparkle weight="fill" size={11} /> Operative PDI
              </div>
              <div
                data-testid="personnel-pulse"
                className="font-display font-extrabold leading-none mt-1 text-[44px] tabular-nums tracking-tighter"
                style={{ color: pulseColor }}
              >
                {pulse}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-[0.16em] mt-1 font-bold" style={{ color: SUBTEXT }}>
                base {role.pdiContribution}
              </div>
            </div>
            <div className="p-4" style={{ background: "#FFFFFF" }}>
              <div className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold mb-1" style={{ color: SUBTEXT }}>
                Current Pursuit
              </div>
              <p className="text-[13px] font-medium leading-relaxed" style={{ color: DEEP_SLATE }}>
                {pursuits}
              </p>
              {operative.hired && (
                <div className="text-[10.5px] font-mono mt-2" style={{ color: SUBTEXT }}>
                  In role since {new Date(operative.hired).toLocaleDateString(undefined, { year: "numeric", month: "short" })}
                </div>
              )}
            </div>
          </div>

          {/* Category focus list */}
          <div data-testid="personnel-focus">
            <div className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold mb-2" style={{ color: SUBTEXT }}>
              Category Focus
            </div>
            {focus.length === 0 ? (
              <div
                className="px-3 py-2 rounded-sm text-[12px] font-mono"
                style={{
                  background: REQUIRES_CATEGORY_FOCUS.has(role.id) ? "#FEF3C7" : "#FFFFFF",
                  color: REQUIRES_CATEGORY_FOCUS.has(role.id) ? AMBER : SUBTEXT,
                  border: `1px solid ${REQUIRES_CATEGORY_FOCUS.has(role.id) ? AMBER + "55" : RULE}`,
                }}
              >
                {REQUIRES_CATEGORY_FOCUS.has(role.id)
                  ? "No UNSPSC focus assigned — this drags Leadership PDI by 10 points."
                  : "Master access · no domain restriction at this tier."}
              </div>
            ) : (
              <ul className="space-y-1.5">
                {focus.map((s) => (
                  <li
                    key={s.segment_code}
                    className="flex items-center gap-3 p-2.5 rounded-sm"
                    style={{ background: "#FFFFFF", border: `1px solid ${RULE}` }}
                  >
                    <span className="font-mono text-[10px] font-bold tabular-nums" style={{ color: SUBTEXT, minWidth: 70 }}>
                      {s.segment_code}
                    </span>
                    <span className="text-[12.5px] font-medium" style={{ color: DEEP_SLATE }}>
                      {s.segment_name}
                    </span>
                    <span
                      className="ml-auto text-[9px] font-mono tracking-[0.18em] uppercase font-bold"
                      style={{ color: s.category === "DIRECT" ? EX_GREEN : SUBTEXT }}
                    >
                      {s.category}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Strategic activity grid */}
          <div data-testid="personnel-activity">
            <div className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold mb-2" style={{ color: SUBTEXT }}>
              Strategic Oversight · Last 30 Days
            </div>
            <div className="grid grid-cols-2 gap-px" style={{ background: RULE }}>
              <ActivityCell label="Decisions made"      value={activity.decisions} />
              <ActivityCell label="Approvals issued"    value={activity.approvals} />
              <ActivityCell label="Attestations signed" value={activity.attestations} />
              <ActivityCell label="AAR submissions"     value={activity.aar} />
            </div>
            <div className="mt-3 px-3 py-2.5 rounded-sm flex items-baseline justify-between gap-2"
                 style={{ background: "#FFFFFF", border: `1px solid ${RULE}` }}>
              <span className="font-mono text-[10.5px] tracking-[0.18em] uppercase font-bold" style={{ color: SUBTEXT }}>
                Weekly spend under their stewardship
              </span>
              <span className="font-mono text-[14px] font-bold tabular-nums" style={{ color: DEEP_SLATE }}>
                {fmtUSD(activity.weeklySpend)}
              </span>
            </div>
          </div>

          {/* Audit trail · recent attested decisions */}
          <div data-testid="personnel-audit-trail">
            <div className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold mb-2" style={{ color: SUBTEXT }}>
              Recent Attested Decisions
            </div>
            <ul className="space-y-2">
              {[
                { id: "PO-2026-A412", note: "Approved Should-Cost retender · 9.4% banked." },
                { id: "PO-2026-A398", note: "Submitted AAR · category lever vs market beat budget." },
                { id: "PO-2026-A381", note: "Refined Strategic Partner narrative for executive briefing." },
              ].slice(0, role.id === "BUYER" ? 2 : 3).map((rec) => (
                <li
                  key={rec.id}
                  className="px-3 py-2 rounded-sm flex items-baseline gap-3"
                  style={{ background: "#FFFFFF", border: `1px solid ${RULE}` }}
                >
                  <span className="font-mono text-[10px] font-bold tracking-[0.14em]" style={{ color: SUBTEXT }}>
                    {rec.id}
                  </span>
                  <span className="text-[12.5px] font-medium leading-relaxed" style={{ color: DEEP_SLATE }}>
                    {rec.note}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <footer
          className="px-5 py-3 border-t flex items-center justify-between"
          style={{ borderColor: RULE, background: "#F3F4F6" }}
        >
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase font-bold" style={{ color: SUBTEXT }}>
            Personnel · {operative.id.toUpperCase()}
          </span>
          <button
            type="button"
            onClick={onClose}
            data-testid="personnel-drawer-close-footer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10.5px] font-mono tracking-[0.16em] uppercase font-bold"
            style={{ background: DEEP_SLATE, color: ALABASTER, border: `1px solid ${DEEP_SLATE}` }}
          >
            Close
          </button>
        </footer>
      </aside>
    </div>
  );
}

function ActivityCell({ label, value }) {
  return (
    <div className="p-3" style={{ background: "#FFFFFF" }}>
      <div className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold" style={{ color: SUBTEXT }}>
        {label}
      </div>
      <div className="font-display font-extrabold text-[24px] tabular-nums tracking-tight mt-1"
           style={{ color: DEEP_SLATE }}>
        {value}
      </div>
    </div>
  );
}

